/**
 * How a shopper request reads in the inbox: filters, who it is from, what it
 * is about, the figures, the status actions. Pure — the list, the drawer and
 * the tests agree on one version.
 */
import type { StatusTone } from '../../../components/design-system/admin/StatusBadge';
import {
    OPEN_STATUSES,
    REQUEST_KINDS,
    REQUEST_STATUSES,
    RequestCounts,
    RequestItem,
    RequestKind,
    RequestListQuery,
    RequestStatus,
    RequestView,
} from './types';

/** A `t` — real (i18n.getFixedT) or a test double. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

// ── Filters ─────────────────────────────────────────────────────────────

export type StatusFilter = 'open' | RequestStatus | 'all';
export const STATUS_FILTERS: StatusFilter[] = ['open', 'new', 'contacted', 'scheduled', 'done', 'cancelled', 'all'];

/** `?status=` → a filter; anything unknown is the default, Open. */
export function parseStatusFilter(raw: string | null | undefined): StatusFilter {
    return raw && (STATUS_FILTERS as string[]).includes(raw) ? (raw as StatusFilter) : 'open';
}

/** `?kind=` → a kind, or none. */
export function parseKindFilter(raw: string | null | undefined): RequestKind | null {
    return raw && (REQUEST_KINDS as readonly string[]).includes(raw) ? (raw as RequestKind) : null;
}

/** The statuses a filter asks the API for; undefined = all of them. */
export function statusesFor(filter: StatusFilter): RequestStatus[] | undefined {
    if (filter === 'all') return undefined;
    if (filter === 'open') return [...OPEN_STATUSES];
    return [filter];
}

/** The count a status pill shows, from the list's counts. */
export function statusFilterCount(filter: StatusFilter, counts: RequestCounts | null | undefined): number {
    const by = counts?.byStatus;
    if (!by) return 0;
    const sum = (statuses: readonly RequestStatus[]) => statuses.reduce((n, s) => n + (by[s] ?? 0), 0);
    if (filter === 'all') return sum(REQUEST_STATUSES);
    if (filter === 'open') return sum(OPEN_STATUSES);
    return by[filter] ?? 0;
}

/** The list's query string: comma-joined filters, as the API reads them. */
export function requestListQueryString(query: RequestListQuery = {}): string {
    const params = new URLSearchParams();
    if (query.status?.length) params.set('status', query.status.join(','));
    if (query.kind?.length) params.set('kind', query.kind.join(','));
    if (query.leadId) params.set('leadId', query.leadId);
    if (query.q?.trim()) params.set('q', query.q.trim());
    if (query.limit) params.set('limit', String(query.limit));
    if (query.before) params.set('before', query.before);
    const s = params.toString();
    return s ? `?${s}` : '';
}

// ── Presentation ────────────────────────────────────────────────────────

/** Colour only where it means something: new needs you, done went well. */
export const STATUS_TONE: Record<RequestStatus, StatusTone> = {
    new: 'warning',
    contacted: 'info',
    scheduled: 'info',
    done: 'success',
    cancelled: 'neutral',
};

/** i18n key segment for a status / kind / source (SHOPPER_REQUESTS.STATUS.NEW…). */
export const upperKey = (value: string): string => value.replace(/-/g, '_').toUpperCase();

const KNOWN_SOURCES = ['visualizer', 'smart-assist', 'shopper-chat', 'showroom-qr', 'share', 'saved-room', 'mcp'];

/** SHOPPER_REQUESTS.SOURCE.<key>; OTHER for a source this build does not know. */
export function sourceKey(source: string | null | undefined): string {
    return source && KNOWN_SOURCES.includes(source) ? upperKey(source) : 'OTHER';
}

/** Who sent it: their name, else their email, else their phone. Empty when none of these. */
export function whoOf(request: Pick<RequestView, 'contact'>): string {
    const c = request.contact ?? {};
    return (c.name || '').trim() || (c.email || '').trim() || (c.phone || '').trim();
}

/** The first product and how many more — the list row's product line. */
export function productLine(items: RequestItem[] | null | undefined): { title: string; more: number; thumbnailUrl?: string } | null {
    const list = (items ?? []).filter((i) => i && (i.title || '').trim());
    if (!list.length) return null;
    return { title: list[0].title.trim(), more: list.length - 1, thumbnailUrl: list[0].thumbnailUrl };
}

// ── Figures ─────────────────────────────────────────────────────────────

const localeOf = (lang?: string | null): string => {
    const l = (lang || 'en').slice(0, 2);
    return ['en', 'fr', 'es', 'ar'].includes(l) ? l : 'en';
};

/** m² at the precision a measured room deserves: one decimal under 10, whole above. */
export function roundArea(m2: number): number {
    return m2 < 10 ? Math.round(m2 * 10) / 10 : Math.round(m2);
}

/** "14" or "12–16" (m² excluded), or null when there is no area. A range wins: it is the honest figure. */
export function areaFigure(areaM2: number | null | undefined, range: [number, number] | null | undefined, lang?: string | null): { kind: 'about' | 'range'; text: string } | null {
    const fmt = (n: number) => new Intl.NumberFormat(localeOf(lang), { maximumFractionDigits: 1 }).format(roundArea(n));
    if (range && Number.isFinite(range[0]) && Number.isFinite(range[1]) && range[1] > 0) {
        const [a, b] = range[0] <= range[1] ? range : [range[1], range[0]];
        if (roundArea(a) !== roundArea(b)) return { kind: 'range', text: `${fmt(a)}–${fmt(b)}` };
        return { kind: 'about', text: fmt(a) };
    }
    if (typeof areaM2 === 'number' && Number.isFinite(areaM2) && areaM2 > 0) return { kind: 'about', text: fmt(areaM2) };
    return null;
}

/** €1,234.50 in the given language; null when the amount or the currency is missing — never a made-up zero. */
export function formatMoney(amount: number | null | undefined, currency: string | null | undefined, lang?: string | null): string | null {
    if (typeof amount !== 'number' || !Number.isFinite(amount) || !currency) return null;
    try {
        return new Intl.NumberFormat(localeOf(lang), { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: 2 }).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
    }
}

/** 'YYYY-MM-DD' as a local date (no timezone shift), or null. */
export function parseLocalDate(value: string | null | undefined): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
}

/** "Sat 25 Oct" (short) or "Saturday 25 October" (long), in the given language. */
export function formatDay(value: string | null | undefined, lang?: string | null, style: 'short' | 'long' = 'short'): string | null {
    const d = parseLocalDate(value);
    if (!d) return null;
    return new Intl.DateTimeFormat(localeOf(lang), { weekday: style, day: 'numeric', month: style }).format(d);
}

// ── Status actions ──────────────────────────────────────────────────────

export interface StatusAction {
    to: RequestStatus;
    /** SHOPPER_REQUESTS.ACTIONS.<key> */
    key: 'MARK_CONTACTED' | 'MARK_SCHEDULED' | 'MARK_DONE' | 'CANCEL' | 'REOPEN';
    destructive?: boolean;
}

/**
 * What the vendor can do next with a request. Scheduled only means something
 * for a showroom visit; a closed request can only be reopened (back to new,
 * where it is seen again).
 */
export function statusActions(kind: RequestKind, status: RequestStatus): StatusAction[] {
    if (status === 'done' || status === 'cancelled') return [{ to: 'new', key: 'REOPEN' }];
    const out: StatusAction[] = [];
    if (status === 'new') out.push({ to: 'contacted', key: 'MARK_CONTACTED' });
    if (kind === 'visit' && status !== 'scheduled') out.push({ to: 'scheduled', key: 'MARK_SCHEDULED' });
    out.push({ to: 'done', key: 'MARK_DONE' });
    out.push({ to: 'cancelled', key: 'CANCEL', destructive: true });
    return out;
}

/** The item rows' area, summed — only when every row with an area says so. */
export function itemsArea(items: RequestItem[] | null | undefined): number | null {
    const withArea = (items ?? []).filter((i) => typeof i.areaM2 === 'number' && Number.isFinite(i.areaM2));
    if (!withArea.length) return null;
    return withArea.reduce((n, i) => n + (i.areaM2 as number), 0);
}

const UNIT_KEYS = ['m2', 'box', 'piece', 'unit', 'linear_m', 'roll'];

/** "m²", "box"… translated; an unknown unit as it came. */
export function unitLabel(unit: string | null | undefined, t: Translate): string {
    if (!unit) return '';
    return UNIT_KEYS.includes(unit) ? t(`SHOPPER_REQUESTS.UNIT.${unit.toUpperCase()}`) : unit;
}
